import React, { useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { beforeAfterImages } from "../mock";

const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onDown = (e) => {
    draggingRef.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(cx);
  };
  const onMove = (e) => {
    if (!draggingRef.current) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(cx);
  };
  const onUp = () => (draggingRef.current = false);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[16/10] md:aspect-[16/8] overflow-hidden select-none bg-neutral-200 cursor-ew-resize"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      <img
        src={beforeAfterImages.after}
        alt="Après"
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      <img
        src={beforeAfterImages.before}
        alt="Avant"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable="false"
      />
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-md bg-black/55 backdrop-blur-sm text-white text-[10px] tracking-[0.25em] uppercase">
        Avant
      </div>
      <div className="absolute top-4 right-4 px-3 py-1.5 rounded-md bg-white/85 backdrop-blur-sm text-neutral-900 text-[10px] tracking-[0.25em] uppercase">
        Après
      </div>
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_10px_rgba(0,0,0,0.25)]"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#9F8B76] shadow-lg flex items-center justify-center">
          <ChevronLeft className="w-4 h-4 text-white" />
          <ChevronRight className="w-4 h-4 text-white -ml-1" />
        </div>
      </div>
    </div>
  );
};

const Realisations = () => {
  const navigate = useNavigate();
  return (
    <section id="realisations" className="bg-[#F7F4EF] pt-40 md:pt-48 pb-24 md:pb-32" data-nav-theme="light">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <h2
          className="font-display uppercase text-neutral-900 leading-[0.95] mb-6"
          style={{ fontSize: "clamp(38px, 5vw, 84px)" }}
        >
          Réalisations
        </h2>
        <p className="text-neutral-600 text-[15px] leading-relaxed max-w-xl">
          De la rénovation complète à la construction neuve, faites glisser
          le curseur pour voir la transformation.
        </p>

        <div className="mt-14">
          <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-500 mb-4 block">
            Rénovation — Appartement parisien
          </span>
          <BeforeAfter />
          <p className="mt-5 text-neutral-600 text-[14px] leading-relaxed max-w-2xl">
            Rénovation complète d'un appartement parisien : peintures,
            parquet et décoration entièrement repensés, une baie vitrée style
            verrière ouvrant la chambre sur le salon après déplacement de la
            cloison pour agrandir l'espace de vie, ainsi qu'une réhabilitation
            complète de la salle de bain et de la cuisine.
          </p>
          <button
            onClick={() => {
              navigate("/contact");
            }}
            className="mt-8 inline-flex items-center gap-3 bg-neutral-900 text-white rounded-full pl-5 pr-1.5 py-1.5 text-[12px] uppercase tracking-widest font-medium hover:bg-[#9F8B76] transition-colors group active:scale-95"
          >
            Nous consulter
            <span className="w-9 h-9 rounded-full bg-[#9F8B76] text-white flex items-center justify-center">
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        </div>

        <div className="mt-24 md:mt-32">
          <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-500 mb-4 block">
            Construction — Maison à Varennes-Jarcy (91)
          </span>

          <div className="grid grid-cols-1 gap-4 md:gap-5">
            <div className="w-full aspect-[6/5] overflow-hidden">
              <img
                src="/assets/realisation-varennes-exterieur.jpg"
                alt="Maison à Varennes-Jarcy, façade extérieure"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="/assets/realisation-varennes-cuisine.jpg"
                  alt="Maison à Varennes-Jarcy, cuisine"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="/assets/realisation-varennes-salon.jpg"
                  alt="Maison à Varennes-Jarcy, salon"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <p className="mt-5 text-neutral-600 text-[14px] leading-relaxed max-w-2xl">
            Maison individuelle de 6 pièces, dont 3 chambres avec salle de
            bain, réalisée à Varennes-Jarcy (91) à l'issue d'une division de
            terrain ayant permis la construction de deux maisons.
          </p>
        </div>

        <div className="mt-24 md:mt-32">
          <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-500 mb-4 block">
            Construction — Maison à Brunoy (91)
          </span>

          <div className="w-full aspect-[3/2] overflow-hidden">
            <img
              src="/assets/realisation-brunoy-exterieur-v2.jpg"
              alt="Maison à Brunoy, façade extérieure"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-brunoy-cuisine.jpg"
                alt="Maison à Brunoy, cuisine"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-brunoy-salon.jpg"
                alt="Maison à Brunoy, salon"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <p className="mt-5 text-neutral-600 text-[14px] leading-relaxed max-w-2xl">
            Maison individuelle de plain-pied avec sous-sol, comprenant 4
            chambres, un bureau, un salon et une cuisine, construite à
            Brunoy (91).
          </p>
        </div>

        <div className="mt-24 md:mt-32">
          <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-500 mb-4 block">
            Petit collectif — Boussy-Saint-Antoine (91)
          </span>

          <div className="w-full aspect-[3/2] overflow-hidden">
            <img
              src="/assets/realisation-boussy-ensemble.jpg"
              alt="Petit collectif à Boussy-Saint-Antoine, vue d'ensemble"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-boussy-lot1.jpg"
                alt="Boussy-Saint-Antoine, façade lot 1"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-boussy-lot2.jpg"
                alt="Boussy-Saint-Antoine, façade lot 2"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <p className="mt-5 text-neutral-600 text-[14px] leading-relaxed max-w-2xl">
            Petit collectif de 4 maisons individuelles, réalisé à
            Boussy-Saint-Antoine (91) à l'issue d'une division parcellaire
            en 4 lots. Un programme qui illustre notre savoir-faire au-delà
            de la maison unique : coordination technique et réglementaire
            de bout en bout, du foncier à la livraison.
          </p>
        </div>

        <div className="mt-24 md:mt-32">
          <span className="uppercase tracking-[0.25em] text-[11px] text-neutral-500 mb-4 block">
            Construction — Maison à Mandres-les-Roses (94)
          </span>

          <div className="w-full aspect-[3/2] overflow-hidden">
            <img
              src="/assets/realisation-mandres-exterieur-v2.jpg"
              alt="Maison à Mandres-les-Roses, façade extérieure"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-mandres-jardin.jpg"
                alt="Maison à Mandres-les-Roses, jardin"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/assets/realisation-mandres-salon.jpg"
                alt="Maison à Mandres-les-Roses, salon"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <p className="mt-5 text-neutral-600 text-[14px] leading-relaxed max-w-2xl">
            Maison individuelle avec 4 chambres, 2 salles de bains, salon,
            cuisine et garage, construite à Mandres-les-Roses (94).
          </p>
        </div>
      </div>
    </section>
  );
};

export default Realisations;
